// GRN phase 1 (2026-10-21): Goods Receiving opens on what is due. Vendor orders created after go-live only
// (g13 start clean): older POs stay on their own pages, exactly as before.
// 2026-10-22, per the approved mockup: tiles with money (due today, partly delivered with no news, rejections to
// resolve), "2 of 12 still coming" lines, "Partly delivered · rest promised …" and a Review button for old balances.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Empty, Spin, Tag, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ExpectedDeliveries as Data, ExpectedDeliveryRow, getExpectedDeliveries, getPO } from '../../api';
import ResponsiveTable from '../responsive/ResponsiveTable';
import ReceiveWindow from './ReceiveWindow';
import { money } from './shared';
import { useRecordPaymentPrompt } from '../payments/useRecordPaymentPrompt';

const MUTED = '#5f6672';
const day = (d: string | null) => (d ? dayjs(d).format('D MMM') : '');

/** The state pill and the small line under it, as on the mockup. */
const stateOf = (r: ExpectedDeliveryRow): { label: string; color: string; sub?: string } => {
  if (r.state === 'stale') return { label: `No delivery for ${r.quietDays} days`, color: 'default', sub: 'chase or close the rest' };
  if (r.state === 'late') return { label: `Late ${r.lateDays} day${r.lateDays === 1 ? '' : 's'}`, color: 'red', sub: r.partly ? 'partly delivered' : undefined };
  if (r.partly) return { label: 'Partly delivered', color: 'orange', sub: r.expected ? `rest promised ${day(r.expected)}` : undefined };
  if (r.state === 'due_today') return { label: 'Due today', color: 'orange' };
  if (r.state === 'this_week') return { label: 'Due this week', color: 'blue' };
  return { label: 'Later', color: 'default' };
};

// Rejected goods step 1 (2026-10-24): the "Rejections to resolve" tile opens Receipts with that filter.
const ExpectedDeliveries: React.FC<{ canReceive: boolean; onShowRejections?: () => void }> = ({ canReceive, onShowRejections }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [receivePo, setReceivePo] = useState<any>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const payPrompt = useRecordPaymentPrompt(); // step 3a: "Record the payment now?" after a receipt

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getExpectedDeliveries()); } catch (e: any) { message.error(e?.response?.data?.error || 'Could not load expected deliveries'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openReceive = async (poId: string) => {
    setOpening(poId);
    try { setReceivePo(await getPO(poId)); } catch (e: any) { message.error(e?.response?.data?.error || 'Could not open the PO'); } finally { setOpening(null); }
  };

  if (loading && !data) return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
  const c = data?.counts;
  const tile = (label: string, n: number, sub: string | undefined, tone: 'plain' | 'bad' | 'attn', onOpen?: () => void) => {
    const color = n > 0 ? (tone === 'bad' ? '#cf1322' : tone === 'attn' ? '#d46b08' : undefined) : undefined;
    const box: React.CSSProperties = { border: `1px solid ${n > 0 && tone !== 'plain' ? (tone === 'bad' ? '#ffccc7' : '#ffe7ba') : '#f0f0f0'}`, borderRadius: 10, padding: '10px 12px', background: n > 0 && tone !== 'plain' ? (tone === 'bad' ? '#fff1f0' : '#fff7e6') : '#fff', minWidth: 0 };
    const body = (
      <>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color }}>{n}</div>
        {sub && <div style={{ fontSize: 12, color: MUTED }}>{sub}</div>}
      </>
    );
    return onOpen && n > 0
      ? <button type="button" onClick={onOpen} title="Show these receipts" style={{ ...box, textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer' }}>{body}</button>
      : <div style={box}>{body}</div>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {c && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          {tile('Due today', c.dueToday, c.dueToday ? `EGP ${money(c.dueTodayValue, 0)}` : undefined, 'plain')}
          {tile('Late', c.late, c.late ? `oldest ${c.lateOldestDays} day${c.lateOldestDays === 1 ? '' : 's'}` : undefined, 'bad')}
          {tile('Due this week', c.thisWeek, undefined, 'plain')}
          {tile('Partly delivered, no news for 14+ days', c.stale, c.stale ? `EGP ${money(c.staleValue, 0)} open` : undefined, 'attn')}
          {tile('Rejections to resolve', c.rejections, c.rejections ? `EGP ${money(c.rejectionsValue, 0)}${c.rejectionReceipts ? ` · on ${c.rejectionReceipts} receipt${c.rejectionReceipts === 1 ? '' : 's'}` : ''}` : undefined, 'attn', onShowRejections)}
        </div>
      )}
      <ResponsiveTable<ExpectedDeliveryRow>
        rowKey="id"
        loading={loading}
        dataSource={data?.rows || []}
        pagination={false}
        size="small"
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No vendor orders waiting. POs created as “Order from vendor” appear here." /> }}
        card={{ header: ['po', 'state'], footer: ['go'] }}
        columns={[
          { title: 'PO', key: 'po', width: 150, render: (_: any, r) => <span><Link to={`/pos/${r.id}`}><code>{r.poId}</code></Link><div style={{ fontSize: 12, color: MUTED }}>{r.mrIds.join(', ')}</div></span> },
          { title: 'Vendor', key: 'vendor', width: 170, render: (_: any, r) => <Link to={`/vendors/${r.vendor.id}`}>{r.vendor.name}</Link> },
          { title: 'Expected', key: 'expected', width: 110, render: (_: any, r) => (r.expected ? day(r.expected) : '—') },
          {
            title: 'Open lines', key: 'lines',
            render: (_: any, r) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {r.openLines.map((l) => (
                  <span key={l.id} style={{ fontSize: 13 }}>
                    <bdi>{l.item}</bdi>
                    {' · '}
                    {l.open !== l.qty ? <><b>{l.open}</b> of {l.qty} {l.unit || ''} still coming</> : <>×<b>{l.open}</b> {l.unit || ''}</>}
                    {l.pendingCancel ? <Tag style={{ marginLeft: 6 }}>waiting for Ahmed</Tag> : null}
                  </span>
                ))}
              </div>
            ),
          },
          { title: 'Open value', key: 'value', width: 110, align: 'right' as const, render: (_: any, r) => money(r.openValue, 0) },
          {
            title: 'State', key: 'state', width: 190,
            render: (_: any, r) => {
              const s = stateOf(r);
              return (
                <span>
                  <Tag color={s.color} style={{ marginInlineEnd: 4 }}>{s.label}</Tag>
                  {r.replacement && <Tag color="orange">replacement</Tag>}
                  {s.sub && <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{s.sub}</div>}
                </span>
              );
            },
          },
          ...(canReceive ? [{
            title: '', key: 'go', width: 110,
            render: (_: any, r: ExpectedDeliveryRow) => r.state === 'stale'
              ? <Button size="small" onClick={() => navigate(`/pos/${r.id}`)}>Review</Button>
              : <Button type={r.state === 'late' || r.state === 'due_today' ? 'primary' : 'default'} size="small" icon={<InboxOutlined />} loading={opening === r.poId} onClick={() => openReceive(r.poId)}>Receive</Button>,
          }] : []),
        ]}
      />
      <div style={{ fontSize: 12, color: MUTED }}>
        Vendor orders created from now on. Older POs, their balances and rejections stay on their own pages as before.
      </div>
      {receivePo && (
        <ReceiveWindow open={!!receivePo} po={receivePo} onClose={() => setReceivePo(null)} onSaved={() => { const p = receivePo?.poId; setReceivePo(null); load(); if (p) payPrompt.ask(p); }} />
      )}
      {payPrompt.element}
    </div>
  );
};

export default ExpectedDeliveries;
